/**
 * One column, chosen from the ones a view setting will accept.
 *
 * Shared by every menu that points a view at a column, so the four of them
 * cannot drift apart in how a choice is made or in what "none" looks like.
 *
 * "None" is always offered. A view can be put back to unconfigured, and it is
 * also the way out of a pair whose other half is keeping the column somebody
 * wants off the list -- a timeline's two ends, a list view's two columns.
 */
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/shared/ui/dropdown-menu'

/** The radio value standing for "no column chosen". */
const NONE = 'none'

export function FieldChoice({
  label,
  chosen,
  candidates,
  noneLabel,
  onChoose,
}: {
  label: string
  chosen: number | null
  candidates: readonly Field[]
  /** What the empty choice is called, when "None" is not what it means. */
  noneLabel?: string
  onChoose: (fieldId: number | null) => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={chosen === null ? NONE : String(chosen)}
        onValueChange={(value) => {
          onChoose(value === NONE ? null : Number(value))
        }}
      >
        <DropdownMenuRadioItem value={NONE}>
          {noneLabel ?? t('view.columnNone')}
        </DropdownMenuRadioItem>
        {candidates.map((field) => (
          <DropdownMenuRadioItem key={field.id} value={String(field.id)}>
            <span className="truncate">{field.name}</span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}
